import {
  captureConsoleIntegration,
  extraErrorDataIntegration,
  httpClientIntegration,
  init,
  reactRouterBrowserTracingIntegration,
  reportingObserverIntegration,
} from '@sentry/react';
import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';

export function loadSentry() {
  init({
    dsn: 'https://93183e6d78b8b453fb6cb2d4c21689b7@o4505984920059904.ingest.sentry.io/4505984933363712',
    enabled: import.meta.env.PROD,
    integrations: [
      captureConsoleIntegration(),
      extraErrorDataIntegration(),
      httpClientIntegration({
        failedRequestStatusCodes: [400, 599],
      }),
      // Default config also captures 'deprecation' and 'intervention' reports, which are mostly
      // noise from third-party scripts and browser extensions (e.g. sync XHR deprecation with
      // sourceFile=null). Narrow to 'crash' so only subframe/worker crashes come through.
      reportingObserverIntegration({ types: ['crash'] }),
      reactRouterBrowserTracingIntegration({
        enableInp: true,
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Apart from API keys, at this moment we don't collect any PII.
    //
    // `sendDefaultPii` is deprecated in @sentry/react 10.70 and removed in v11. This is its exact
    // equivalent: per the SDK's own resolveDataCollectionOptions, supplying `dataCollection` at all
    // selects a DEFAULTS base that already matches what `sendDefaultPii: true` expanded to, with
    // one exception — frameContextLines, which the legacy mapping pinned to 7 (the value the
    // ContextLines integration uses) rather than the default 5. userInfo is stated explicitly
    // because the resolver defaults it to true while the type's doc comment claims false.
    dataCollection: {
      userInfo: true,
      frameContextLines: 7,
    },

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/themavericklab\.com/],

    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 100% of the transactions, reduce in production!

    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 1%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Ignore errors not from our code
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Generic error code from errors outside the security sandbox
      // You can delete this if using raven.js > 1.0, which ignores these automatically.
      'Script error.',
      // Avast extension error
      '_avast_submit',
      // Other errors not from our code
      'msDiscoverChatAvailable',
      '__gCrWeb',
    ],
    denyUrls: [
      // Google Adsense
      /pagead\/js/i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      // Firefox extensions
      /^resource:\/\//i,
      // Webkit extensions
      /.*webkit-masked-url:\/\/.*/i,
      /.*safari-web-extension:\/\/.*/i,
    ],
  });
}
